import { combineReducers } from 'redux';
import { isBoolean } from 'lodash';

import { getTodayLocal } from './reportsHelper';
import * as types from './constants';
import barberFilter from '../../common/reducers/barberFilter';
import payoutHistory from './reducers/payoutHistory';
import products from './products/reducer';
import brandFilters from './reducers/brandFilters';
import shopServicesCount from './reducers/shopServicesCount';
import discountAmount from './reducers/discountAmount';
import serviceFilters from './reducers/serviceFilters';
import productCategoryFilters from './reducers/productCategoryFilters';
import saleReport from './barbersReport/reducer';
import serviceCategoryFilter from './reducers/serviceCategoryFilter';
import dashboard from './dash/reducer';
import filters from './filters/reducer';
import { mapPayoutReport } from './helpers';
import itemsReport from './itemsReport/reducer';
import breakdown from './breakdown/reducer';
import metrics from './metrics/reducer';
import refundsAndDiscounts from './refundsAndDiscounts/reducer';
import shopFilters from './filters/ShopFilter/reducers';
import quickBooks from './deposits/reducer';
import customReports from './customReports/reducers';
import { INVENTORY_ACTIONS } from '../../products/products/constants';
import { CHANGE_SHOP } from '../../auth/constants';
import { UPDATE_SHOP_FILTER, SHOP_FILTER_SELECT_ALL, SHOP_FILTER_DESELECT_ALL } from './filters/ShopFilter/constants';
import config from '../../../config';

const barberReports = (state = [], action) => {
  switch (action.type) {
    case types.GET_BARBER_REPORTS:
      return action.reports;
    default:
      return state;
  }
};

const barberReportSummary = (state = [], action) => {
  switch (action.type) {
    case types.GET_BARBER_REPORT_SUMMARY:
      return action.reportSummary;
    default:
      return state;
  }
};

const payoutReportState = { productReport: [], serviceReport: [] };
const payoutReportsNoCommissionSplit = (state = payoutReportState, action) => {
  switch (action.type) {
    case types.GET_PAYOUT_REPORT_NO_COMMISSION_SPLIT:
      return mapPayoutReport(action.reports);
    default:
      return state;
  }
};

const appointments = (state = [], action) => {
  switch (action.type) {
    case types.IMPORT_REPORT_APPOINTMENTS: {
      return action.appointments;
    }
    case types.RESET_REPORTS:
      return [];
    default:
      return state;
  }
};

const csv = (state = '', action) => {
  switch (action.type) {
    case types.SET_CSV:
      return action.csv;
    default:
      return state;
  }
};

const startDate = (state = getTodayLocal(config.defaultTimezone), action) => {
  switch (action.type) {
    case types.SET_REPORTS_START_DATE:
      return action.date;
    case types.RESET_DATE_FILTERS:
      return getTodayLocal(config.defaultTimezone);
    case CHANGE_SHOP:
      return action.shop ? getTodayLocal(action.shop.timezone) : getTodayLocal(config.defaultTimezone);
    default:
      return state;
  }
};

const endDate = (state = getTodayLocal(config.defaultTimezone), action) => {
  switch (action.type) {
    case types.SET_REPORTS_END_DATE:
      return action.date;
    case types.RESET_DATE_FILTERS:
      return getTodayLocal(config.defaultTimezone);
    case CHANGE_SHOP:
      return action.shop ? getTodayLocal(action.shop.timezone) : getTodayLocal(config.defaultTimezone);
    default:
      return state;
  }
};

const depositsReport = (state = [], action) => {
  switch (action.type) {
    case types.GET_DEPOSITS_REPORT:
      return [...state, ...action.payload];
    case types.CLEAR_DEPOSITS_REPORT:
      return [];
    default:
      return state;
  }
};

const payoutsReports = (state = {}, { type, payload }) => {
  switch (type) {
    case types.FETCH_PAYOUTS_REPORT:
      return payload;
    case types.CLEAR_PAYOUTS_REPORT:
      return {};
    default:
      return state;
  }
};

const payoutsReportsLastPayout = (state = null, { type, payload }) => {
  switch (type) {
    case types.FETCH_LAST_PAYOUT:
      return payload || null;
    case types.CLEAR_PAYOUTS_REPORT:
      return null;
    default:
      return state;
  }
};

const productSalesReport = (state = [], { type, payload }) => {
  switch (type) {
    case types.FETCH_PRODUCT_SALES_REPORT:
      return payload;
    default:
      return state;
  }
};

const paymentFiltersState = [
  { title: 'Via Squire', checked: true, type: 'payment', slug: 'squire' },
  { title: 'POS', checked: true, type: 'payment', slug: 'pos' },
  { title: 'Cash/Other', checked: true, type: 'payment', slug: 'cash' },
  { title: 'Unpaid', checked: true, type: 'payment', slug: 'unpaid' },
];

const paymentFilters = (state = paymentFiltersState, action) => {
  switch (action.type) {
    case types.UPDATE_PAYMENT_FILTER:
      return state.map((filter) => {
        if (filter === action.payload) {
          // eslint-disable-next-line no-param-reassign
          filter.checked = !filter.checked;
        }
        return filter;
      });
    default:
  }
  return state;
};

const simpleFilterReducer = (actionType, defaultState) => (state = defaultState, action) => {
  switch (action.type) {
    case actionType: {
      // select/deselect all case
      if (isBoolean(action.payload)) {
        state.forEach((filter) => {
          // eslint-disable-next-line no-param-reassign
          filter.checked = action.payload;
        });
        return [...state];
      }

      return state.map((filter) => {
        if (filter === action.payload) {
          // eslint-disable-next-line no-param-reassign
          filter.checked = !filter.checked;
        }
        return filter;
      });
    }
    default:
  }

  return state;
};

const statusFiltersState = [
  { title: 'Paid', checked: true, type: 'status' },
  { title: 'Refunded', checked: true, type: 'status' },
];

const statusFilters = simpleFilterReducer(types.UPDATE_STATUS_FILTER, statusFiltersState);

const apptBookedByFiltersState = [
  { title: 'Client', checked: true, type: 'booking', slug: 'customer' },
  { title: 'Barber', checked: true, type: 'booking', slug: 'barber' },
  { title: 'Shop', checked: true, type: 'booking', slug: 'shop' },
  { title: 'Unknown', checked: true, type: 'booking', slug: 'unknown' },
];

const appointmentBookedByFilters = simpleFilterReducer(
  types.UPDATE_APPOINTMENT_BOOKED_BY_FILTER, apptBookedByFiltersState
);

const apptStatusFilterState = [
  { title: 'Upcoming', checked: true, type: 'status', slug: 'upcoming' },
  { title: 'Completed', checked: true, type: 'status', slug: 'completed' },
  { title: 'Cancelled', checked: true, type: 'status', slug: 'cancelled' },
  { title: 'No-show', checked: true, type: 'status', slug: 'no-show' },
];

const appointmentStatusFilters = simpleFilterReducer(
  types.UPDATE_APPOINTMENT_STATUS_FILTER, apptStatusFilterState
);

const apptPlatformFilterState = [
  { title: 'Mobile App', checked: true, type: 'platform' },
  { title: 'Widget', checked: true, type: 'platform' },
  { title: 'Web', checked: true, type: 'platform' },
  { title: 'Commander', checked: true, type: 'platform' },
  { title: 'Google', checked: true, type: 'platform' },
  { title: 'Unknown', checked: true, type: 'platform' },
];

const appointmentPlatformFilters = simpleFilterReducer(
  types.UPDATE_APPOINTMENT_PLATFORM_FILTER, apptPlatformFilterState
);

const filtersDirty = (state = false, action) => {
  switch (action.type) {
    case types.UPDATE_APPOINTMENT_STATUS_FILTER:
    case types.UPDATE_APPOINTMENT_BOOKED_BY_FILTER:
    case types.UPDATE_APPOINTMENT_PLATFORM_FILTER:
    case types.UPDATE_PAYMENT_FILTER:
    case types.UPDATE_PRODUCT_INVENTORY_ACTION_FILTER:
    case types.ADD_BARBER_FILTER_REPORT:
    case types.REMOVE_BARBER_FILTER_REPORT:
    case types.SHOW_ALL_BARBER_FILTERS_REPORT:
    case types.HIDE_ALL_BARBER_FILTERS_REPORT:
    case types.SET_REPORT_FILTERS_DIRTY:
    case UPDATE_SHOP_FILTER:
    case SHOP_FILTER_SELECT_ALL:
    case SHOP_FILTER_DESELECT_ALL:
      return true;
    case types.SET_REPORTS_START_DATE:
    case types.SET_REPORTS_END_DATE:
      return false;
    default:
      return state;
  }
};

const productInventoryActionState = INVENTORY_ACTIONS.map(action => ({
  title: action.label,
  checked: true,
  value: action.value,
}));

const productInventoryActionFilters = simpleFilterReducer(
  types.UPDATE_PRODUCT_INVENTORY_ACTION_FILTER, productInventoryActionState
);

const loanRepaymentReport = (state = [], action) => {
  switch (action.type) {
    case types.GET_LOAN_REPAYMENT_REPORT:
      return action.reports;
    default:
      return state;
  }
};

export default combineReducers({
  barberReports,
  barberReportSummary,
  payoutReportsNoCommissionSplit,
  appointments,
  csv,
  startDate,
  endDate,
  barberFilter: barberFilter({
    add: types.ADD_BARBER_FILTER_REPORT,
    remove: types.REMOVE_BARBER_FILTER_REPORT,
    show: types.SHOW_ALL_BARBER_FILTERS_REPORT,
    hide: types.HIDE_ALL_BARBER_FILTERS_REPORT,
    includeUnassigned: true,
  }),
  depositsReport,
  payoutsReports,
  payoutsReportsLastPayout,
  productSalesReport,
  paymentFilters,
  payoutHistory,
  products,
  appointmentBookedByFilters,
  appointmentStatusFilters,
  appointmentPlatformFilters,
  productInventoryActionFilters,
  filtersDirty,
  statusFilters,
  brandFilters,
  productCategoryFilters,
  shopServicesCount,
  discountAmount,
  serviceFilters,
  saleReport,
  serviceCategoryFilter,
  dashboard,
  loanRepaymentReport,
  filters,
  itemsReport,
  breakdown,
  metrics,
  refundsAndDiscounts,
  shopFilters,
  quickBooks,
  customReports,
});
